import { BCMSDate, BCMSCurrency } from "@bcm-dev/bcms-vite";
import { Account } from "model";
/**
 * @class TASPositivePayCheckForm
 * @classdesc Class definition for controlling a check
 * form which will then be transformed into a positive pay check.
 * 
 * @property {STRING} _id - The id of the check form.
 * @property {STRING} _account - The account of the check form.
 * @property {STRING} _issuedDate - The issued date of the check form.
 * @property {STRING} _checkNumber - The check number of the check form.
 * @property {NUMBER} _amount - The amount of the check form.
 * @property {STRING} _payee - The payee of the check form.
 * @property {STRING} _referenceNumber - The reference number of the check form.
 * @property {STRING} _type - The type of the check form.
 * @property {STRING} _status - The current status of the check form.
 * @property {BOOLEAN} _mobileViewIsOpen - Tells us if the mobile view is open in the check form.
 * @property {BOOLEAN} _mobileViewIsEditing - Tells us if the mobile view is being edited in the check form.
 * @property {BOOLEAN} _showTypeDropdown - Tells use if the type select is shown/open.
 */
export default class TASPositivePayCheckForm {
    constructor(record) {
        this.id = record.id;
        this.account = record.account;
        this.issuedDate = record.issuedDate;
        this.checkNumber = record.checkNumber;
        this.amount = record.amount;
        this.payee = record.payee;
        this.referenceNumber = record.referenceNumber;
        this.type = record.type;
        this.status = record.status;
        this.mobileViewIsOpen = record.mobileViewIsOpen;
        this.mobileViewIsEditing = record.mobileViewIsEditing;
        this.showTypeDropdown = record.showTypeDropdown;
    }

    /**
     * @name id
     * @instance
     * @property set {STRING} - sets the id
     * @memberof TASPositivePayCheckForm
     */
    set id(value) { this._id = value; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account.
     * @memberof TASPositivePayCheckForm
     */
    set account(val) {
        if(val) {
            if(val.id) {
                this._account = val;
            } else {
                this._account = Account.initByType(val);
            }
        } else {
            this._account = val;
        }
    }

    /**
     * @name issuedDate
     * @instance
     * @property set {STRING} - sets the issuedDate
     * @memberof TASPositivePayCheckForm
     */
    set issuedDate(value) {
        if(value instanceof BCMSDate) {
            this._issuedDate = value;
        } else if(!isNaN(value)) {
            this._issuedDate = BCMSDate.init().offset(value)
        } else {
            this._issuedDate = value;
        }
    }

    /**
     * @name checkNumber
     * @instance
     * @property set {STRING} - sets the checkNumber
     * @memberof TASPositivePayCheckForm
     */
    set checkNumber(value) { this._checkNumber = value; }

    /**
     * @name mobileViewIsOpen
     * @instance
     * @property set {STRING} - sets the mobileViewIsOpen
     * @memberof TASPositivePayCheckForm
     */
    set mobileViewIsOpen(value) { this._mobileViewIsOpen = value; }

    /**
     * @name mobileViewIsEditing
     * @instance
     * @property set {STRING} - sets the mobileViewIsEditing
     * @memberof TASPositivePayCheckForm
     */
    set mobileViewIsEditing(value) { this._mobileViewIsEditing = value; }

    /**
     * @name amount
     * @instance
     * @property set {STRING} - sets the amount
     * @memberof TASPositivePayCheckForm
     */
    set amount(value) { 
        if(value) {
            if(value instanceof BCMSCurrency) {
                this._amount = value;
            } else {
                this._amount = BCMSCurrency.init(value);
            }
        } else {
            this._amount = value;
        }
     }

    /**
     * @name payee
     * @instance
     * @property set {STRING} - sets the payee
     * @memberof TASPositivePayCheckForm
     */
    set payee(value) { this._payee = value; }

    /**
     * @name referenceNumber
     * @instance
     * @property set {STRING} - sets the referenceNumber
     * @memberof TASPositivePayCheckForm
     */
    set referenceNumber(value) { this._referenceNumber = value; }

    /**
     * @name type
     * @instance
     * @property set {STRING} - sets the type
     * @memberof TASPositivePayCheckForm
     */
    set type(value) { this._type = value; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the status.
     * @memberof TASPositivePayCheckForm
     */
    set status(value) { this._status = value; }

    /**
     * @name showTypeDropdown
     * @instance
     * @property get {STRING} - returns the dropdown ui status.
     * @memberof TASPositivePayCheckForm
     */
    set showTypeDropdown(value) {
        this._showTypeDropdown = value;
    }

    /**
     * @name id
     * @instance
     * @property get {STRING} - returns the id
     * @memberof TASPositivePayCheckForm
     */
    get id() { return this._id; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account.
     * @memberof TASPositivePayCheckForm
     */
    get account() {
        return this._account;
    }

    /**
     * @name issuedDate
     * @instance
     * @property get {STRING} - returns the issuedDate
     * @memberof TASPositivePayCheckForm
     */
    get issuedDate() { return this._issuedDate; }

    /**
     * @name checkNumber
     * @instance
     * @property get {STRING} - returns the checkNumber
     * @memberof TASPositivePayCheckForm
     */
    get checkNumber() { return this._checkNumber; }

    /**
     * @name mobileViewIsOpen
     * @instance
     * @property get {STRING} - returns the mobileViewIsOpen
     * @memberof TASPositivePayCheckForm
     */
    get mobileViewIsOpen() { return this._mobileViewIsOpen; }

    /**
     * @name mobileViewIsEditing
     * @instance
     * @property get {STRING} - returns the mobileViewIsEditing
     * @memberof TASPositivePayCheckForm
     */
    get mobileViewIsEditing() { return this._mobileViewIsEditing; }

    /**
     * @name amount
     * @instance
     * @property get {STRING} - returns the amount
     * @memberof TASPositivePayCheckForm
     */
    get amount() { return this._amount; }

    /**
     * @name payee
     * @instance
     * @property get {STRING} - returns the payee
     * @memberof TASPositivePayCheckForm
     */
    get payee() { return this._payee; }

    /**
     * @name referenceNumber
     * @instance
     * @property get {STRING} - returns the referenceNumber
     * @memberof TASPositivePayCheckForm
     */
    get referenceNumber() { return this._referenceNumber; }

    /**
     * @name type
     * @instance
     * @property get {STRING} - returns the type
     * @memberof TASPositivePayCheckForm
     */
    get type() { return this._type; }

    /**
     * @name status
     * @instance
     * @property get {STRING} - returns the status.
     * @memberof TASPositivePayCheckForm
     */
    get status() { return this._status; }

    /**
     * @name showTypeDropdown
     * @instance
     * @property get {STRING} - returns the dropdown ui status.
     * @memberof TASPositivePayCheckForm
     */
    get showTypeDropdown() {
        return this._showTypeDropdown;
    }

    /**
     * @name canDelete
     * @instance
     * @property get {BOOLEAN} - returns true if the check can be deleted.
     * @memberof TASPositivePayCheckForm
     */
    get canDelete() {
        return this.status !== "reconciled";
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayCheckForm
     */
    get record() {
        const issuedDate = this.issuedDate instanceof BCMSDate ? this.issuedDate.diff() : this.issuedDate;
        const amount = this.amount instanceof BCMSCurrency ? this.amount.amount : this.amount;

        return {
            id: this.id,
            account: this.account,
            issuedDate: issuedDate,
            checkNumber: this.checkNumber,
            amount: amount,
            payee: this.payee,
            referenceNumber: this.referenceNumber,
            type: this.type,
            status: this.status,
            mobileViewIsOpen: this.mobileViewIsOpen,
            mobileViewIsEditing: this.mobileViewIsEditing,
            showTypeDropdown: this.showTypeDropdown
        };
    }

    /**
     * @function reset
     * @instance
     * @property {VOID} - No parameters.
     * @description Returns an instance of the class with default values.
     * @memberof TASPositivePayCheckForm
     */
    reset() {
        const defaultVals = TASPositivePayCheckForm.defaultVals();
        return TASPositivePayCheckForm.init(defaultVals);
    }

    /** 
     * @function defaultVals   
     * @static
     * @property {VOID} - No parameters
     * @description Returns the default values of the class.
     * @memberof TASPositivePayCheckForm 
     */
    static defaultVals() {
        return { 
            id: "0",
            account: undefined,
            issuedDate: "0",
            checkNumber: "", 
            amount: "",
            payee: "",
            referenceNumber: "",
            type: "issue",
            status: "not reconciled",
            mobileViewIsOpen: true, 
            mobileViewIsEditing: true,
            showTypeDropdown: false
        };
    }
    
    /** 
     * @function init   
     * @static
     * @property {OBJECT} - An object to initialize an instance of the class.
     * @description Returns an instance of the class.
     * @memberof TASPositivePayCheckForm 
     */
    static init(record) {
        const defaultVals = TASPositivePayCheckForm.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayCheckForm(vals);
    }
}